import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import SyncConfirmationDialog from './SyncConfirmationDialog';
import LinkExistingDialog from './LinkExistingDialog';
import SyncFailureAlert from './SyncFailureAlert';
import { apiClient } from '../services/api';
import { formatCurrency } from '../utils/formatCurrency';
import { describeSyncStatus } from '../utils/syncStatus';
import { isSyncBlocked, syncBlockedDetail } from '../utils/syncEligibility';
import { formatStripeAmount, formatDate } from '../utils/formatStripe';
import type { StripeProductInfo, StripeTier, DealMapping, BillingAccount } from '../types/api';

/**
 * Deal detail view for HubSpot iframe modal.
 * Compact, chrome-less version of DealDetail.tsx.
 */
const HubSpotDealDetail: React.FC = () => {
  const { dealId } = useParams<{ dealId: string }>();
  const [isSyncing, setIsSyncing] = useState(false);
  const [showSyncConfirmation, setShowSyncConfirmation] = useState(false);
  const [showLinkDialog, setShowLinkDialog] = useState(false);

  const { data: deal, isLoading, refetch } = useQuery({
    queryKey: ['deal', dealId],
    queryFn: () => apiClient.getDealDetail(dealId!),
    enabled: !!dealId,
  });

  const [searchParams] = useSearchParams();
  // Active iframe JWT. Seeded from the URL token (same one HubSpotRoute installs)
  // so the first render is unchanged; the picker swaps it at runtime.
  const [activeToken, setActiveToken] = useState<string | null>(
    () => searchParams.get('token'),
  );
  const [switchError, setSwitchError] = useState<string | null>(null);
  // The tenant the picker currently reflects — tracked independently of the
  // cached `deal`, because a failed refetch leaves `deal` on the PREVIOUS
  // tenant while the token has already moved. Seeded null; set to the deal's
  // resolved tenant once loaded (see effect below) and updated on a successful
  // switch. Drives the Select value and the no-op guard.
  const [activeTenantId, setActiveTenantId] = useState<string | null>(null);
  // True while a switch is in flight: disables the picker and the Sync/Link
  // mutations so nothing can act against a half-swapped token/data pair.
  const [isSwitching, setIsSwitching] = useState(false);

  // Install the active token on every render so a post-switch token wins over
  // the getter HubSpotRoute installed (both target the same apiClient singleton;
  // last writer wins). Seeded from the URL token, so this is a no-op initially.
  useEffect(() => {
    if (activeToken) {
      apiClient.setTokenGetter(() => activeToken);
    }
  }, [activeToken]);

  // Keep activeTenantId in sync with the loaded deal's resolved tenant on first
  // load. After a successful switch we set it explicitly (below) so the picker
  // updates immediately; this effect covers the initial load.
  useEffect(() => {
    if (deal?.tenant_id && activeTenantId === null) {
      setActiveTenantId(deal.tenant_id);
    }
  }, [deal?.tenant_id, activeTenantId]);

  const handleAccountSwitch = async (tenantId: string) => {
    if (tenantId === (activeTenantId ?? deal?.tenant_id)) return;
    setSwitchError(null);
    setIsSwitching(true);
    // Snapshot the current token so we can roll back if the switch's refetch fails.
    const previousToken = activeToken;
    try {
      const { token } = await apiClient.switchIframeAccount(tenantId);
      // Install the new token synchronously BEFORE refetch (setActiveToken only
      // schedules a re-render; the token effect runs after commit, later than
      // when refetch()'s axios request reads the getter).
      apiClient.setTokenGetter(() => token);
      setActiveToken(token);
      // react-query v5 refetch() RESOLVES with a result (it does not throw on
      // error unless throwOnError is set), so inspect the result explicitly.
      const result = await refetch();
      if (result.isError) {
        throw result.error ?? new Error('Failed to load the selected account.');
      }
      // Success: commit the picker to the new tenant.
      setActiveTenantId(tenantId);
    } catch (err) {
      console.error('Account switch failed:', err);
      // Roll back to the previous account so the token and the rendered data
      // (still the previous tenant's, retained by react-query) stay consistent.
      apiClient.setTokenGetter(() => previousToken ?? undefined);
      setActiveToken(previousToken);
      setSwitchError('Could not switch account. The previous account is still shown.');
    } finally {
      setIsSwitching(false);
    }
  };

  const handleSyncClick = () => {
    setShowSyncConfirmation(true);
  };

  const handleSync = async () => {
    if (!dealId) return;
    // Close the confirmation dialog immediately on confirm; the sync progress is
    // shown on the "Sync Now" button (isSyncing). Mirrors LinkExistingDialog,
    // whose confirm handler owns its own close.
    setShowSyncConfirmation(false);
    setIsSyncing(true);
    try {
      await apiClient.syncDeal(dealId);
      await refetch();
    } catch (err) {
      console.error('Sync failed:', err);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleClose = () => {
    window.parent.postMessage({ action: 'DONE' }, '*');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Only blank the whole view when there's no deal data at all (initial load
  // failed). A refetch error AFTER a successful load — e.g. switching to an
  // account where this deal 404s — keeps the last-good data (react-query retains
  // it) so the header + account picker stay mounted and the user can switch back.
  // Post-switch refetch failures surface inline via switchError instead.
  if (!deal) {
    return (
      <div className="p-4">
        <Alert variant="destructive">
          <AlertDescription>Failed to load deal details.</AlertDescription>
        </Alert>
      </div>
    );
  }

  const isSynced = deal.sync_status === 'synced' && deal.stripe_subscription_id;
  // A `linked` deal carries a real Stripe subscription Finrite didn't create —
  // treat it like synced: never offer to (re-)sync or (re-)link it.
  const isLinked = deal.sync_status === 'linked' && !!deal.stripe_subscription_id;
  const isSubAttached = isSynced || isLinked;
  // One-off invoice link state — independent of the subscription side above.
  // A deal can be invoice-linked/billed without ever having a subscription.
  const isInvoiceLinked =
    deal.one_off_invoice_status === 'linked' && !!deal.one_off_invoice_id;
  const isInvoiceBilled = deal.one_off_invoice_status === 'synced';
  // Gates "Sync Now": a deal attached on EITHER side (subscription or invoice)
  // shouldn't be offered a sync that would no-op or duplicate.
  const isAttached = isSubAttached || isInvoiceLinked || isInvoiceBilled;
  const status = describeSyncStatus(deal.sync_status, {
    isSubscriptionAttached: !!isSubAttached,
    isInvoiceLinked,
    isInvoiceBilled,
  });
  // Per-pool link gating: the two pools resolve independently, so a deal with
  // (say) a confirmed subscription but a still-pending invoice candidate must
  // still be able to open the dialog to resolve the invoice side, and vice
  // versa. "Link existing…" is available whenever EITHER pool is unresolved.
  const canLinkSub = !isSubAttached;
  const canLinkInvoice = !(isInvoiceLinked || isInvoiceBilled);
  const canLink = canLinkSub || canLinkInvoice;
  // Candidate subscriptions discovered for an unconfirmed deal — a "possible
  // link". When present, the link action reads "Confirm link…" instead of
  // "Link existing…". Invoice candidates give the same nudge when the deal
  // isn't already attached on the invoice side.
  const linkCandidates = deal.potential_historic_mapping?.candidates ?? [];
  const invoiceLinkCandidates = deal.potential_invoice_mapping?.candidates ?? [];
  const hasCandidates =
    (canLinkSub && linkCandidates.length > 0) ||
    (canLinkInvoice && invoiceLinkCandidates.length > 0);
  const dashboardBaseUrl = deal.stripe_dashboard_base_url ?? null;
  const sub = deal.stripe_subscription;

  // Period rendering: an active/trialing subscription has a meaningful current
  // period; once it ends (canceled, scheduled-cancel done) the relevant range
  // is start_date → ended_at instead.
  const subscriptionEnded = sub?.status === 'canceled' && !!sub.ended_at;
  const periodStart = subscriptionEnded ? sub?.start_date : sub?.current_period_start;
  const periodEnd = subscriptionEnded ? sub?.ended_at : sub?.current_period_end;
  const periodLabel = subscriptionEnded ? 'Subscription period' : 'Period';

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">{deal.dealname || 'Unnamed Deal'}</h1>
          <div className="text-sm text-muted-foreground flex items-center gap-2">
            {deal.amount && <span>{formatCurrency(deal.amount, deal.deal_currency_code)}</span>}
            {/* Label, not the raw id — same fallback as DealDetail: the id
                ("closedwon", or an opaque number on a custom pipeline) shows
                only when HubSpot's pipelines couldn't be read. */}
            {deal.dealstage && (
              <Badge variant="secondary">
                {deal.dealstage_label ?? deal.dealstage}
              </Badge>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {(deal.available_accounts?.length ?? 0) > 1 && (
            <Select
              value={activeTenantId ?? deal.tenant_id ?? deal.available_accounts![0].tenant_id}
              onValueChange={handleAccountSwitch}
              disabled={isSwitching}
            >
              <SelectTrigger className="w-[200px] h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {deal.available_accounts!.map((a) => (
                  <SelectItem key={a.tenant_id} value={a.tenant_id}>
                    {accountOptionLabel(a)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button variant="outline" size="sm" onClick={handleClose}>
            Close
          </Button>
        </div>
      </div>
      {switchError && (
        <p className="text-sm text-destructive">{switchError}</p>
      )}

      {/* Sync Status */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Sync Status</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Badge variant={status.variant}>{status.label}</Badge>
              {deal.stripe_subscription_id && (
                <span className="text-xs text-muted-foreground font-mono">
                  {deal.stripe_subscription_id}
                </span>
              )}
            </div>
            {(!isAttached || canLink) && (
              <div className="flex items-center gap-2">
                {!isAttached && (
                  // Same gate as the full detail page: the deal can be open
                  // with only a quote, which the API refuses to sync.
                  <Button
                    onClick={handleSyncClick}
                    disabled={isSyncing || isSwitching || isSyncBlocked(deal)}
                    title={isSyncBlocked(deal) ? syncBlockedDetail(deal) : undefined}
                    size="sm"
                  >
                    {isSyncing ? (
                      <>
                        <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                        Syncing...
                      </>
                    ) : (
                      'Sync Now'
                    )}
                  </Button>
                )}
                {canLink && (
                  <Button
                    onClick={() => setShowLinkDialog(true)}
                    disabled={isSyncing || isSwitching}
                    variant="outline"
                    size="sm"
                  >
                    {hasCandidates ? 'Confirm link…' : 'Link existing…'}
                  </Button>
                )}
              </div>
            )}
          </div>
          {/* Kept inside the status card: the error used to sit at the foot of
              the modal, several cards below the badge that prompts the look. */}
          {status.isFailed && (
            <SyncFailureAlert
              errorMessage={deal.error_message}
              lastSynced={deal.last_synced}
              className="mt-3"
              size="compact"
            />
          )}
        </CardContent>
      </Card>

      {/* Contract — the signed document Finrite manages for this deal. Shown
          only when the deal's property holds a usable http(s) URL; the backend
          nulls anything else, so this is never an unvalidated href inside the
          HubSpot iframe. Sits above Contacts, matching the full detail page,
          because it is the thing a person opening this card came to find. */}
      {deal.signed_doc_url && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Contract</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-sm bg-muted p-2 rounded-md">
              <a
                className="text-primary underline"
                href={deal.signed_doc_url}
                target="_blank"
                rel="noreferrer"
              >
                Signed document
              </a>
            </div>
          </CardContent>
        </Card>
      )}

      {/* HubSpot Contacts */}
      {deal.contacts && deal.contacts.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">HubSpot Contacts ({deal.contacts.length})</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {deal.contacts.map((contact) => (
              <div key={contact.id} className="text-sm bg-muted p-2 rounded-md">
                <div className="font-medium">{contact.firstname} {contact.lastname}</div>
                {contact.email && (
                  <div className="text-muted-foreground">{contact.email}</div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Stripe Customer */}
      {deal.stripe_customer && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Stripe Customer</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="text-sm bg-muted p-2 rounded-md space-y-0.5">
              {deal.stripe_customer.name && (
                <div className="font-medium">{deal.stripe_customer.name}</div>
              )}
              {deal.stripe_customer.email && (
                <div className="text-muted-foreground">{deal.stripe_customer.email}</div>
              )}
              <div className="text-xs text-muted-foreground font-mono">
                {dashboardBaseUrl ? (
                  <a
                    className="text-primary underline"
                    href={`${dashboardBaseUrl}/customers/${deal.stripe_customer.id}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {deal.stripe_customer.id}
                  </a>
                ) : (
                  deal.stripe_customer.id
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Line Items */}
      {deal.line_items && deal.line_items.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Line Items ({deal.line_items.length})</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {deal.line_items.map((item) => (
              <div key={item.id} className="text-sm bg-muted p-2 rounded-md space-y-1">
                <div className="flex justify-between">
                  <span className="font-medium">{item.name || 'Unnamed Item'}</span>
                  <span className="text-muted-foreground">
                    {item.quantity && `${item.quantity} × `}
                    {item.price && formatCurrency(item.price, item.currency)}
                  </span>
                </div>
                {(item.stripe_product_name || item.stripe_price_id) && (
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span className="truncate">
                      {item.stripe_product_name && (
                        <span className="mr-2">{item.stripe_product_name}</span>
                      )}
                      {item.stripe_price_id && (
                        dashboardBaseUrl ? (
                          <a
                            className="text-primary underline font-mono"
                            href={`${dashboardBaseUrl}/prices/${item.stripe_price_id}`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {item.stripe_price_id}
                          </a>
                        ) : (
                          <span className="font-mono">{item.stripe_price_id}</span>
                        )
                      )}
                    </span>
                    {item.price_matches === true && (
                      <Badge variant="default" className="ml-2">Matched</Badge>
                    )}
                    {item.price_matches === false && (
                      <Badge variant="secondary" className="ml-2">Override</Badge>
                    )}
                  </div>
                )}
              </div>
            ))}
            {/* TODO: show discount % / $ per line item once discount handling lands. */}
          </CardContent>
        </Card>
      )}

      {/* Pricing (usage-based deals only) */}
      {deal.stripe_products && deal.stripe_products.length > 0 &&
        (deal.billing_model === 'metered' || deal.billing_model === 'metered_subscription') && (
        <PricingCard products={deal.stripe_products} />
      )}

      {/* Stripe Subscription Details (when synced) */}
      {sub && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Stripe Subscription</CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-1">
            <div>
              <span className="text-muted-foreground">Status: </span>
              <Badge variant={sub.status === 'active' ? 'default' : 'secondary'}>
                {sub.status}
              </Badge>
            </div>
            <div>
              <span className="text-muted-foreground">Customer: </span>
              {dashboardBaseUrl ? (
                <a
                  className="text-primary underline font-mono text-xs"
                  href={`${dashboardBaseUrl}/customers/${sub.customer_id}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {sub.customer_id}
                </a>
              ) : (
                <span className="font-mono text-xs">{sub.customer_id}</span>
              )}
            </div>
            <div>
              <span className="text-muted-foreground">ID: </span>
              {dashboardBaseUrl ? (
                <a
                  className="text-primary underline font-mono text-xs"
                  href={`${dashboardBaseUrl}/subscriptions/${sub.id}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {sub.id}
                </a>
              ) : (
                <span className="font-mono text-xs">{sub.id}</span>
              )}
            </div>
            <div>
              <span className="text-muted-foreground">{periodLabel}: </span>
              <span>
                {formatDate(periodStart)} → {formatDate(periodEnd)}
              </span>
            </div>
            {sub.cancel_at_period_end && !subscriptionEnded && (
              <div>
                <span className="text-muted-foreground">Scheduled to cancel at period end</span>
              </div>
            )}
            {sub.cancel_at && !subscriptionEnded && (
              <div>
                <span className="text-muted-foreground">Cancels: </span>
                <span>{formatDate(sub.cancel_at)}</span>
              </div>
            )}
            {sub.billing_cycle_anchor && (
              <div>
                <span className="text-muted-foreground">Billing cycle anchor: </span>
                <span>{formatDate(sub.billing_cycle_anchor)}</span>
              </div>
            )}
            {sub.collection_method && (
              <div>
                <span className="text-muted-foreground">Collection method: </span>
                <span>{sub.collection_method}</span>
              </div>
            )}
            {sub.proration_behavior && (
              <div>
                <span className="text-muted-foreground">Proration: </span>
                <span>{sub.proration_behavior}</span>
              </div>
            )}
            <SubscriptionMetadata metadata={sub.metadata} />
            {/* TODO: render discounts / coupons list here once discount handling lands. */}
          </CardContent>
        </Card>
      )}

      {/* Invoices */}
      {deal.invoices && deal.invoices.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Invoices ({deal.invoices.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Number</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {deal.invoices.map((inv) => (
                  <TableRow key={inv.id}>
                    <TableCell className="font-mono text-xs">{inv.number ?? inv.id}</TableCell>
                    <TableCell>
                      <Badge variant={inv.status === 'paid' ? 'default' : 'secondary'}>
                        {inv.status ?? '—'}
                      </Badge>
                    </TableCell>
                    <TableCell>{formatStripeAmount(inv.amount_due, inv.currency)}</TableCell>
                    <TableCell>{formatDate(inv.created)}</TableCell>
                    <TableCell className="text-right">
                      {inv.hosted_invoice_url && (
                        <a
                          className="text-primary underline text-xs"
                          href={inv.hosted_invoice_url}
                          target="_blank"
                          rel="noreferrer"
                        >
                          View
                        </a>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Payments */}
      {deal.payments && deal.payments.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Payments ({deal.payments.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {deal.payments.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell>{formatStripeAmount(p.amount, p.currency)}</TableCell>
                    <TableCell>
                      <Badge variant={p.status === 'succeeded' ? 'default' : 'secondary'}>
                        {p.status ?? '—'}
                      </Badge>
                    </TableCell>
                    <TableCell>{p.payment_method_type ?? '—'}</TableCell>
                    <TableCell>{formatDate(p.created)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Upcoming Invoice */}
      {deal.upcoming_invoice && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Upcoming Invoice</CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-1">
            <div>
              <span className="text-muted-foreground">Amount due: </span>
              <span>
                {formatStripeAmount(
                  deal.upcoming_invoice.amount_due,
                  deal.upcoming_invoice.currency,
                )}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Period: </span>
              <span>
                {formatDate(deal.upcoming_invoice.period_start)} →{' '}
                {formatDate(deal.upcoming_invoice.period_end)}
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Usage (metered deals only) */}
      {deal.usage && deal.usage.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Usage</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Meter</TableHead>
                  <TableHead>Scope</TableHead>
                  <TableHead>Aggregated</TableHead>
                  <TableHead>Period</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {deal.usage.map((u, i) => (
                  <TableRow key={`${u.meter_id}-${u.scope}-${i}`}>
                    <TableCell className="font-mono text-xs">{u.meter_id}</TableCell>
                    <TableCell>
                      <Badge variant={u.scope === 'paid' ? 'default' : 'secondary'}>
                        {u.scope}
                      </Badge>
                    </TableCell>
                    <TableCell>{u.aggregated_value ?? '—'}</TableCell>
                    <TableCell>
                      {formatDate(u.period_start)} → {formatDate(u.period_end)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* The sync error is rendered in the Sync Status card, next to the badge. */}

      {/* Account context: which Finrite tenant + Stripe account this deal
          resolved to. Tenant links to the Finrite web app; the Stripe account
          links to the account-scoped Stripe Dashboard (shown only when one is
          on file). */}
      {(deal.tenant_id || deal.stripe_account_id) && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Account</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {deal.tenant_id && (
              <div className="flex items-center justify-between gap-3">
                <span className="text-muted-foreground">Tenant</span>
                {deal.finrite_web_url ? (
                  <a
                    className="text-primary underline font-mono text-xs break-all text-right"
                    href={deal.finrite_web_url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {deal.tenant_id}
                  </a>
                ) : (
                  <span className="font-mono text-xs break-all text-right">
                    {deal.tenant_id}
                  </span>
                )}
              </div>
            )}
            {deal.stripe_account_id && (
              <div className="flex items-center justify-between gap-3">
                <span className="text-muted-foreground">Stripe account</span>
                <a
                  className="text-primary underline font-mono text-xs break-all text-right"
                  href={buildStripeAccountUrl(dashboardBaseUrl, deal.stripe_account_id)}
                  target="_blank"
                  rel="noreferrer"
                >
                  {deal.stripe_account_id}
                </a>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <SyncConfirmationDialog
        open={showSyncConfirmation}
        dealId={dealId!}
        onOpenChange={setShowSyncConfirmation}
        onConfirm={handleSync}
        isSyncing={isSyncing}
      />

      {showLinkDialog && (
        <LinkExistingDialog
          deal={{
            hubspot_deal_id: deal.deal_id,
            dealname: deal.dealname,
            sync_status: deal.sync_status,
          } as DealMapping}
          open={showLinkDialog}
          onOpenChange={(o) => {
            setShowLinkDialog(o);
            // Re-pull the deal so a just-confirmed link flips the status/subscription.
            if (!o) refetch();
          }}
          activeTenantId={activeTenantId ?? deal.tenant_id}
          unresolvedSides={{ subscription: canLinkSub, invoice: canLinkInvoice }}
        />
      )}
    </div>
  );
};

// "Acme Live (Live)" / "Acme Test (Test)" — mirrors the card's accountOptionLabel.
function accountOptionLabel(account: BillingAccount): string {
  return `${account.label} (${account.is_test ? 'Test' : 'Live'})`;
}

// Account-scoped Stripe Dashboard URL for an acct_… id. When the mode is known
// (from stripe_dashboard_base_url, e.g. ".../test"), splice the account id
// between the origin and the mode segment → https://dashboard.stripe.com/{acct}/test.
// Otherwise fall back to the bare account URL (Stripe resolves the mode itself).
function buildStripeAccountUrl(
  dashboardBaseUrl: string | null | undefined,
  accountId: string,
): string {
  if (dashboardBaseUrl) {
    const match = dashboardBaseUrl.match(/^(https?:\/\/[^/]+)(\/.*)?$/);
    if (match) return `${match[1]}/${accountId}${match[2] ?? ''}`;
  }
  return `https://dashboard.stripe.com/${accountId}`;
}

// Convert a Stripe amount to a display string. Handles three shapes:
//   - `unit_amount` (integer cents)
//   - `unit_amount_decimal` (string, sub-cent precision like "0.05")
//   - nothing (returns null)
function formatStripeUnit(
  unitAmount: number | null | undefined,
  unitAmountDecimal: string | null | undefined,
  currency: string | null | undefined,
): string | null {
  if (typeof unitAmount === 'number') {
    return formatCurrency(unitAmount / 100, currency ?? undefined);
  }
  if (unitAmountDecimal) {
    const parsed = parseFloat(unitAmountDecimal);
    if (!Number.isNaN(parsed)) {
      // Sub-cent values benefit from more decimals; default locale rounds to 2.
      const code = (currency ?? 'USD').toUpperCase();
      try {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: code,
          minimumFractionDigits: 2,
          maximumFractionDigits: 6,
        }).format(parsed / 100);
      } catch {
        return `${(parsed / 100).toString()} ${code}`;
      }
    }
  }
  return null;
}

function formatInterval(
  interval: string | null | undefined,
  count: number | null | undefined,
): string {
  if (!interval) return '';
  if (!count || count === 1) return interval;
  return `${count} ${interval}s`;
}

function formatTierUpTo(upTo: StripeTier['up_to']): string {
  if (upTo === null || upTo === undefined || upTo === 'inf') return '∞';
  return Number(upTo).toLocaleString();
}

// Card showing per-product usage rates (metered) and flat fees (recurring) for
// usage-based deals. Rendered only when billing_model is metered* so
// non-metered subscriptions keep their cleaner layout.
const PricingCard: React.FC<{ products: StripeProductInfo[] }> = ({ products }) => {
  const meteredProducts = products.filter((p) => p.billing_type === 'metered');
  const flatFeeProducts = products.filter((p) => p.billing_type === 'recurring');

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Pricing</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {meteredProducts.length > 0 && (
          <div className="space-y-2">
            <div className="text-xs font-semibold uppercase text-muted-foreground">
              Usage rates
            </div>
            {meteredProducts.map((p) => (
              <MeteredRate key={p.price_id} product={p} />
            ))}
          </div>
        )}
        {flatFeeProducts.length > 0 && (
          <div className="space-y-2">
            <div className="text-xs font-semibold uppercase text-muted-foreground">
              Flat fees
            </div>
            {flatFeeProducts.map((p) => (
              <FlatFee key={p.price_id} product={p} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const MeteredRate: React.FC<{ product: StripeProductInfo }> = ({ product }) => {
  const interval = formatInterval(product.recurring_interval, product.recurring_interval_count);

  // Per-unit metered price: single rate per unit.
  if (product.billing_scheme === 'per_unit' || !product.billing_scheme) {
    const rate = formatStripeUnit(product.price_amount, product.unit_amount_decimal, product.price_currency);
    return (
      <div className="bg-muted rounded-md p-2 space-y-0.5">
        <div className="font-medium">{product.product_name}</div>
        <div className="text-muted-foreground">
          {rate ? `${rate} per unit` : 'Rate unavailable'}
          {interval && <span> · billed per {interval}</span>}
        </div>
        {product.stripe_meter_id && (
          <div className="text-xs text-muted-foreground font-mono">
            Meter: {product.stripe_meter_id}
          </div>
        )}
      </div>
    );
  }

  // Tiered pricing: render the tier structure.
  return (
    <div className="bg-muted rounded-md p-2 space-y-1">
      <div className="flex justify-between items-center">
        <span className="font-medium">{product.product_name}</span>
        <span className="text-xs text-muted-foreground">
          {product.tiers_mode ?? 'tiered'}
          {interval && ` · per ${interval}`}
        </span>
      </div>
      {product.tiers && product.tiers.length > 0 ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Up to</TableHead>
              <TableHead>Per unit</TableHead>
              <TableHead>Flat</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {product.tiers.map((tier, i) => (
              <TableRow key={i}>
                <TableCell>{formatTierUpTo(tier.up_to)}</TableCell>
                <TableCell>
                  {formatStripeUnit(tier.unit_amount, tier.unit_amount_decimal, product.price_currency) ?? '—'}
                </TableCell>
                <TableCell>
                  {formatStripeUnit(tier.flat_amount, tier.flat_amount_decimal, product.price_currency) ?? '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <div className="text-xs text-muted-foreground">Tiers unavailable</div>
      )}
      {product.stripe_meter_id && (
        <div className="text-xs text-muted-foreground font-mono">
          Meter: {product.stripe_meter_id}
        </div>
      )}
    </div>
  );
};

const FlatFee: React.FC<{ product: StripeProductInfo }> = ({ product }) => {
  const amount = formatStripeUnit(product.price_amount, product.unit_amount_decimal, product.price_currency);
  const interval = formatInterval(product.recurring_interval, product.recurring_interval_count);
  return (
    <div className="bg-muted rounded-md p-2 flex justify-between items-center">
      <span className="font-medium">{product.product_name}</span>
      <span className="text-muted-foreground">
        {amount ?? '—'}
        {interval && ` / ${interval}`}
      </span>
    </div>
  );
};

// Collapse subscription metadata into a disclosure when it has more than a
// few entries — keeps the compact modal from getting overwhelmed.
const SubscriptionMetadata: React.FC<{ metadata?: Record<string, unknown> }> = ({ metadata }) => {
  const entries = Object.entries(metadata ?? {});
  if (entries.length === 0) return null;

  const renderRows = () =>
    entries.map(([key, value]) => (
      <div key={key} className="flex justify-between text-xs">
        <span className="text-muted-foreground font-mono">{key}</span>
        <span className="font-mono truncate ml-2">{String(value)}</span>
      </div>
    ));

  if (entries.length <= 3) {
    return <div className="pt-1 space-y-0.5">{renderRows()}</div>;
  }

  return (
    <Collapsible>
      <CollapsibleTrigger className="text-xs text-muted-foreground underline">
        Metadata ({entries.length})
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-1 space-y-0.5">{renderRows()}</CollapsibleContent>
    </Collapsible>
  );
};

export default HubSpotDealDetail;
